import { Component, ViewEncapsulation, input, computed, inject } from '@angular/core';
import { NgTemplateOutlet, NgOptimizedImage } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

export type CardItemVariant = 'base' | 'image' | 'thumb-image' | 'headed';
export type CardItemColorTheme = 'orange' | 'dark-blue' | 'blue';
export type CardItemLinkStyle = 'arrow' | 'icon' | 'text';

export interface CardItemDecorator {
  type: 'number' | 'label' | 'icon' | 'photo';
  /** For 'number': value is the number string, style is 'colored' or 'circled'. For 'photo': 'square' renders rectangular (partner logos). */
  value?: string;
  style?: 'colored' | 'circled' | 'square';
  /** For 'icon': svgContent is inline SVG markup */
  svgContent?: string;
  /** For 'photo': src and alt for the rounded image */
  src?: string;
  alt?: string;
  /** Intrinsic dimensions for NgOptimizedImage — required when `type === 'photo'` with a Strapi media src */
  width?: number;
  height?: number;
}

export interface CardItemData {
  variant?: CardItemVariant;
  theme?: CardItemColorTheme;
  subhead: string;
  body?: string;
  decorator?: CardItemDecorator;
  topRightLabel?: string;
  link?: {
    href: string;
    text?: string;
    style: CardItemLinkStyle;
    iconSvg?: string;
  };
  /** Image variant specific */
  imageSrc?: string;
  imageAlt?: string;
  /**
   * Intrinsic dimensions of the card image. Required by NgOptimizedImage in
   * Plan 03. When `imageSrc` is present these should also be present — the
   * adapter layer sources them from the Strapi media object. Zero values
   * indicate the image is either missing or bypasses optimisation (SVG).
   */
  imageWidth?: number;
  imageHeight?: number;
  /** Headed variant specific */
  heading?: string;
  footer?: string;
  /** Adds accent top border to emphasize card */
  highlight?: boolean;
}

@Component({
  selector: 'ds-card-item',
  templateUrl: './card-item.html',
  styleUrl: './card-item.scss',
  encapsulation: ViewEncapsulation.None,
  imports: [NgTemplateOutlet, NgOptimizedImage],
})
export class CardItemComponent {
  private readonly sanitizer = inject(DomSanitizer);
  readonly data = input.required<CardItemData>();

  readonly trustedIconSvg = computed((): SafeHtml | null => {
    const svg = this.data().decorator?.svgContent;
    return svg ? this.sanitizer.bypassSecurityTrustHtml(svg) : null;
  });

  readonly trustedLinkIconSvg = computed((): SafeHtml | null => {
    const svg = this.data().link?.iconSvg;
    return svg ? this.sanitizer.bypassSecurityTrustHtml(svg) : null;
  });

  readonly cssClass = computed(() => {
    const d = this.data();
    const classes = ['s__card-item'];
    if (d.theme) {
      classes.push(`s__card-item--theme-${d.theme}`);
    }
    const variant = d.variant ?? 'base';
    if (variant !== 'base') {
      classes.push(`s__card-item--${variant}`);
    }
    if (d.link) {
      classes.push('s__card-item--clickable');
    }
    if (d.highlight) {
      classes.push('s__card-item--highlight');
    }
    return classes.join(' ');
  });

}
